import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, Heart, Maximize2, Sparkles, Camera, Bookmark, Share2, Filter, Star, Music, Palette, ArrowDown, Zap, Moon, Cloud, Target, Sun } from 'lucide-react';

const Moment = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [likedImages, setLikedImages] = useState(new Set([1, 4, 7]));
  const [activeFilter, setActiveFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [hoveredImage, setHoveredImage] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [gradientAngle, setGradientAngle] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [animationPhase, setAnimationPhase] = useState(0);
  const containerRef = useRef(null);

  const images = [
     { id: 1, src: 'image2.jpeg', alt: 'Beautiful moment 1', caption: '“Little hands, pure grace, and a heart full of innocence 🌸✨”', category: 'portrait', date: '2024-01-20' },
    { id: 2, src: 'image3.jpeg', alt: 'Beautiful moment 2', caption: '“Walking through fields of sunshine 🌼🌿”', category: 'urban', date: '2024-02-01' },
    { id: 3, src: 'image4.jpeg', alt: 'Beautiful moment 3', caption: '“Quiet moments, endless horizons 🌿☁️”', category: 'nature', date: '2024-02-10' },
     { id:4, src: 'image6.jpeg', alt: 'Beautiful moment 4', caption: '“Grace in every petal 🌺✨”', category: 'urban', date: '2024-02-20' },
    { id: 5, src: 'image7.jpeg', alt: 'Beautiful moment 5', caption: '“A little love carried along the way 🌺💖”', category: 'nature', date: '2024-03-01' },
    { id: 6, src: 'image8.jpeg', alt: 'Beautiful moment 6', caption: '“Soft blooms, quiet beauty 🌼🍃”', category: 'portrait', date: '2024-03-05' },
    { id:7, src: 'image9.jpeg', alt: 'Beautiful moment 7', caption: '“Reflections of grace under soft lights ✨🌙”', category: 'urban', date: '2024-03-10' },
  ];

  const categories = [
    { id: 'all', label: 'All Moments', count: images.length },
    { id: 'nature', label: 'Nature', count: images.filter(img => img.category === 'nature').length },
    { id: 'portrait', label: 'Portrait', count: images.filter(img => img.category === 'portrait').length },
    { id: 'urban', label: 'Urban', count: images.filter(img => img.category === 'urban').length },
  ];

  const filteredImages = activeFilter === 'all' 
    ? images 
    : images.filter(img => img.category === activeFilter);

  // Mouse move effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 30 - 15,
        y: (e.clientY / window.innerHeight) * 30 - 15
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Scroll progress effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const heroHeight = containerRef.current?.clientHeight || 800;
      const progress = (scrollTop / heroHeight) * 100;
      setScrollProgress(Math.min(progress, 100));
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Animated gradient colors
  useEffect(() => {
    const interval = setInterval(() => {
      setGradientAngle((prev) => (prev + 0.5) % 360);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  // Animation phase for wave effect
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationPhase((prev) => (prev + 0.5) % 360);
    }, 30);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  // Generate floating elements data
  const generateFloatingElements = (count, isMobile) => {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * (isMobile ? 20 : 30) + 10,
      delay: Math.random() * 5,
      duration: Math.random() * 20 + 15,
      type: i % 7, // 0-6 for different element types
      rotation: Math.random() * 360
    }));
  };

  const [floatingElements, setFloatingElements] = useState([]);
  
  useEffect(() => {
    setFloatingElements(generateFloatingElements(40, window.innerWidth < 768));
  }, []);

  const toggleLike = (id, e) => {
    e?.stopPropagation();
    const newLikedImages = new Set(likedImages);
    if (likedImages.has(id)) {
      newLikedImages.delete(id);
    } else {
      newLikedImages.add(id);
    }
    setLikedImages(newLikedImages);
  };

  const openLightbox = (image) => {
    setSelectedImage(image);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  const navigateImage = (direction) => {
    if (!selectedImage) return;
    
    const currentIndex = filteredImages.findIndex(img => img.id === selectedImage.id);
    let newIndex;
    
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % filteredImages.length;
    } else {
      newIndex = (currentIndex - 1 + filteredImages.length) % filteredImages.length;
    }
    
    setSelectedImage(filteredImages[newIndex]);
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  return (
    <>
      <section 
        ref={containerRef}
        id="moments" 
        className="min-h-screen relative overflow-hidden bg-gradient-to-br from-pink-50 via-rose-50 to-pink-100 py-16 px-4 sm:px-6 lg:px-8"
        style={{
          '--gradient-angle': `${gradientAngle}deg`,
          '--animation-phase': `${animationPhase}deg`,
          background: `radial-gradient(circle at ${mousePosition.x + 50}% ${mousePosition.y + 50}%, rgba(255, 182, 193, 0.3) 0%, transparent 60%),
                      radial-gradient(circle at ${100 - mousePosition.x + 50}% ${100 - mousePosition.y + 50}%, rgba(251, 207, 232, 0.35) 0%, transparent 60%),
                      linear-gradient(var(--gradient-angle), rgba(255, 240, 245, 0.95) 0%, rgba(255, 228, 230, 0.85) 50%, rgba(255, 240, 245, 0.95) 100%)`
        }}
      >
        {/* Dynamic background grid */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(rgba(255, 182, 193, 0.15) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(255, 182, 193, 0.15) 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
            maskImage: 'radial-gradient(circle at center, black 30%, transparent 70%)'
          }} />
        </div>

        {/* Animated wave background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-t from-pink-200/10 via-transparent to-transparent animate-wave"></div>
        </div>

        {/* Floating Bubbles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {floatingElements.filter(el => el.type === 0).map((bubble) => (
            <div
              key={`bubble-${bubble.id}`}
              className="absolute rounded-full animate-bubble-float"
              style={{
                left: `${bubble.x}%`,
                top: `${bubble.y}%`,
                width: `${bubble.size}px`,
                height: `${bubble.size}px`,
                background: `radial-gradient(circle at 30% 30%, 
                  rgba(255, 255, 255, 0.9) 0%, 
                  rgba(255, 255, 255, 0.7) 30%,
                  rgba(255, 200, 221, 0.4) 70%,
                  transparent 100%
                )`,
                border: '1px solid rgba(255, 255, 255, 0.5)',
                animationDelay: `${bubble.delay}s`,
                animationDuration: `${bubble.duration}s`,
                filter: 'blur(0.5px)',
                boxShadow: 'inset 0 0 15px rgba(255, 255, 255, 0.6), 0 0 25px rgba(255, 182, 193, 0.4)',
                willChange: 'transform, opacity'
              }}
            />
          ))}
        </div>

        {/* Floating Stars */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {floatingElements.filter(el => el.type === 1).map((star) => (
            <div
              key={`star-${star.id}`}
              className="absolute animate-star-twinkle"
              style={{
                left: `${star.x}%`,
                top: `${star.y}%`,
                width: `${star.size / 2}px`,
                height: `${star.size / 2}px`,
                background: `radial-gradient(circle, 
                  rgba(255, 255, 255, 0.95) 0%,
                  rgba(255, 255, 255, 0.8) 30%,
                  rgba(255, 182, 193, 0.6) 70%,
                  transparent 100%
                )`,
                clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
                animationDelay: `${star.delay}s`,
                animationDuration: `${star.duration}s`,
                filter: 'drop-shadow(0 0 3px rgba(255, 182, 193, 0.6))',
                willChange: 'transform, opacity, filter'
              }}
            />
          ))}
        </div>

        {/* Sparkle Particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {floatingElements.filter(el => el.type === 2).map((sparkle) => (
            <div
              key={`sparkle-${sparkle.id}`}
              className="absolute animate-sparkle-float"
              style={{
                left: `${sparkle.x}%`,
                top: `${sparkle.y}%`,
                width: `${sparkle.size / 3}px`,
                height: `${sparkle.size / 3}px`,
                background: `radial-gradient(circle, 
                  rgba(255, 255, 255, 1) 0%,
                  rgba(255, 255, 255, 0.9) 50%,
                  rgba(244, 114, 182, 0.5) 100%
                )`,
                animationDelay: `${sparkle.delay}s`,
                animationDuration: `${Math.random() * 3 + 2}s`,
                transform: `rotate(${sparkle.rotation}deg)`,
                filter: 'blur(0.5px)',
                willChange: 'transform, opacity'
              }}
            />
          ))}
        </div>

        {/* Floating Hearts */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {floatingElements.filter(el => el.type === 3).map((heart) => (
            <div
              key={`heart-${heart.id}`}
              className="absolute animate-heart-float"
              style={{
                left: `${heart.x}%`,
                top: `${heart.y}%`,
                width: `${heart.size / 2}px`,
                height: `${heart.size / 2}px`,
                background: `linear-gradient(135deg, 
                  rgba(255, 100, 150, 0.3) 0%,
                  rgba(255, 150, 200, 0.2) 50%,
                  rgba(255, 100, 150, 0.1) 100%
                )`,
                clipPath: 'polygon(50% 0%, 100% 35%, 82% 100%, 50% 75%, 18% 100%, 0% 35%)',
                animationDelay: `${heart.delay}s`,
                animationDuration: `${heart.duration}s`,
                filter: 'blur(1px)',
                willChange: 'transform, opacity'
              }}
            />
          ))}
        </div>

        {/* Floating Diamonds */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {floatingElements.filter(el => el.type === 4).map((diamond) => (
            <div
              key={`diamond-${diamond.id}`}
              className="absolute animate-diamond-float"
              style={{
                left: `${diamond.x}%`,
                top: `${diamond.y}%`,
                width: `${diamond.size / 2}px`,
                height: `${diamond.size / 2}px`,
                background: `linear-gradient(135deg, 
                  rgba(255, 255, 255, 0.9) 0%,
                  rgba(244, 114, 182, 0.5) 50%,
                  rgba(236, 72, 153, 0.3) 100%
                )`,
                clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
                animationDelay: `${diamond.delay}s`,
                animationDuration: `${diamond.duration}s`,
                transform: `rotate(${diamond.rotation}deg)`,
                filter: 'drop-shadow(0 0 4px rgba(244, 114, 182, 0.4))',
                willChange: 'transform, opacity'
              }}
            />
          ))}
        </div>

        {/* Floating Music Notes */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {floatingElements.filter(el => el.type === 5).map((note) => (
            <div
              key={`note-${note.id}`}
              className="absolute animate-music-note"
              style={{
                left: `${note.x}%`,
                top: `${note.y}%`,
                fontSize: `${note.size / 10}px`,
                animationDelay: `${note.delay}s`,
                animationDuration: `${note.duration}s`,
                color: 'rgba(244, 114, 182, 0.3)',
                filter: 'drop-shadow(0 0 2px rgba(255, 182, 193, 0.4))',
                willChange: 'transform, opacity'
              }}
            >
              ♫
            </div>
          ))}
        </div>

        {/* Animated gradient orbs */}
        <div 
          className="absolute top-20 left-10 w-48 h-48 bg-gradient-to-br from-pink-400/25 via-rose-400/25 to-pink-400/15 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-orb-float hidden md:block"
          style={{
            transform: `translate(${mousePosition.x * 0.4}px, ${mousePosition.y * 0.4}px) rotate(${mousePosition.x * 0.5}deg)`,
            animationDelay: '0.5s'
          }}
        />
        
        <div 
          className="absolute bottom-32 right-10 w-56 h-56 bg-gradient-to-tr from-rose-300/30 via-pink-300/30 to-rose-300/20 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-orb-float-slow hidden md:block"
          style={{
            transform: `translate(${-mousePosition.x * 0.3}px, ${-mousePosition.y * 0.3}px) rotate(${-mousePosition.x * 0.3}deg)`,
            animationDelay: '1s'
          }}
        />

        {/* Animated decorative icons with trails */}
        <div className="hidden md:block">
          <div className="absolute top-20 right-20 group cursor-pointer">
            <div className="absolute inset-0 bg-pink-400/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
            <Sparkles 
              className="relative text-pink-400/60 w-10 h-10 animate-spin-slow group-hover:text-pink-500 group-hover:scale-110 transition-all duration-500"
              style={{ transform: `translate(${mousePosition.x * 0.1}px, ${mousePosition.y * 0.1}px)` }}
            />
          </div>
          
          <div className="absolute bottom-32 left-20 group cursor-pointer">
            <div className="absolute inset-0 bg-rose-400/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
            <Heart 
              className="relative text-rose-400/70 w-8 h-8 animate-pulse group-hover:text-rose-500 group-hover:scale-110 transition-all duration-500"
              style={{ transform: `translate(${mousePosition.x * 0.2}px, ${mousePosition.y * 0.2}px)` }}
            />
          </div>
          
          <div className="absolute top-32 left-1/3 group cursor-pointer">
            <div className="absolute inset-0 bg-pink-500/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
            <Star 
              className="relative text-pink-500/50 w-9 h-9 animate-spin group-hover:text-pink-600 group-hover:scale-110 transition-all duration-500"
              style={{ transform: `translate(${mousePosition.x * 0.15}px, ${mousePosition.y * 0.15}px)` }}
            />
          </div>
        </div>

        {/* Scroll progress indicator with glow effect */}
        <div className="fixed top-0 left-0 right-0 h-1 z-50">
          <div 
            className="h-full bg-gradient-to-r from-pink-500 via-rose-500 to-pink-500 transition-all duration-300 shadow-lg"
            style={{ width: `${scrollProgress}%` }}
          />
          <div 
            className="absolute top-0 h-full w-full bg-gradient-to-r from-pink-400/50 via-rose-400/50 to-pink-400/50 blur-lg transition-all duration-300"
            style={{ width: `${scrollProgress}%` }}
          />
        </div>

        {/* Header with enhanced animation */}
        <div className="max-w-7xl mx-auto text-center mb-12 relative animate-slide-up-stagger">
          <div className="inline-flex items-center gap-2 sm:gap-3 mb-4 relative">
            {/* Animated border */}
            <div className="absolute -inset-2 sm:-inset-3 bg-gradient-to-r from-pink-400 via-rose-400 to-pink-400 animate-gradient-x rounded-full blur-lg opacity-30"></div>
            
            <div className="w-6 sm:w-8 md:w-12 h-px bg-gradient-to-r from-transparent via-pink-400 to-transparent"></div>
            <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-pink-500 animate-pulse relative" />
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 tracking-tight relative">
              Captured <span className="bg-gradient-to-r from-pink-500 via-rose-500 to-pink-500 bg-clip-text text-transparent animate-gradient-flow">Moments</span>
              <span className="absolute -bottom-1 left-0 right-0 h-px bg-gradient-to-r from-transparent via-pink-400 to-transparent animate-width-pulse"></span>
            </h2>
            <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-pink-500 animate-pulse relative" />
            <div className="w-6 sm:w-8 md:w-12 h-px bg-gradient-to-l from-transparent via-pink-400 to-transparent"></div>
          </div>
          <p className="text-base sm:text-lg text-gray-600 max-w-xs sm:max-w-md md:max-w-2xl mx-auto animate-fade-in-stagger mt-6 relative px-4" style={{ animationDelay: '200ms' }}>
            <span className="relative inline-block">
              A visual journey through elegant moments and cherished memories
              <div className="absolute -bottom-1 left-1/4 right-1/4 h-px bg-gradient-to-r from-transparent via-pink-300 to-transparent animate-width-pulse-slow"></div>
            </span>
          </p>
        </div>

        {/* Filter buttons with floating effect */}
        <div className="max-w-4xl mx-auto mb-8 sm:mb-12 animate-fade-in-stagger" style={{ animationDelay: '300ms' }}>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 relative">
            {categories.map((category, index) => (
              <button
                key={category.id}
                onClick={() => setActiveFilter(category.id)}
                className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full font-medium transition-all duration-500 transform hover:scale-105 animate-button-float group relative overflow-hidden ${
                  activeFilter === category.id
                    ? 'bg-gradient-to-r from-pink-500 via-rose-500 to-pink-500 text-white shadow-lg'
                    : 'bg-white/80 backdrop-blur-sm text-gray-600 hover:bg-white hover:shadow-md'
                }`}
                style={{ animationDelay: `${index * 100 + 400}ms` }}
              >
                {/* Hover effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                
                <div className="flex items-center gap-1 sm:gap-2 text-sm sm:text-base relative">
                  {activeFilter === category.id && <Filter className="w-3 h-3 sm:w-4 sm:h-4 animate-pulse" />}
                  <span>{category.label}</span>
                  <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-xs transition-all duration-500 ${
                    activeFilter === category.id 
                      ? 'bg-white/20 backdrop-blur-sm' 
                      : 'bg-pink-100 text-pink-600 group-hover:bg-pink-200'
                  }`}>
                    {category.count}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Image Grid - Responsive Masonry Layout */}
        {isLoading ? (
          <div className="max-w-6xl mx-auto animate-fade-in-stagger">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 p-2 sm:p-4">
              {[...Array(9)].map((_, index) => (
                <div key={index} className="animate-pulse-slow">
                  <div className="aspect-[3/4] sm:aspect-[4/3] bg-gradient-to-br from-pink-100 via-rose-100 to-pink-100 rounded-xl sm:rounded-2xl relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent transform -translate-x-full animate-shimmer"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="max-w-7xl mx-auto px-2 sm:px-4 animate-fade-in-stagger" style={{ animationDelay: '500ms' }}>
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 sm:gap-6">
              {filteredImages.map((image, index) => (
                <div 
                  key={image.id}
                  className="group relative mb-4 sm:mb-6 break-inside-avoid rounded-xl sm:rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-700 transform hover:-translate-y-2 cursor-pointer overflow-hidden animate-card-float"
                  onClick={() => openLightbox(image)}
                  onMouseEnter={() => setHoveredImage(image.id)}
                  onMouseLeave={() => setHoveredImage(null)}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {/* Image Container */}
                  <div className="relative overflow-hidden">
                    {/* Glow effect on hover */}
                    <div className="absolute inset-0 bg-gradient-to-r from-pink-400/0 via-rose-400/0 to-pink-400/0 group-hover:from-pink-400/20 group-hover:via-rose-400/20 group-hover:to-pink-400/20 transition-all duration-700 rounded-xl sm:rounded-2xl"></div>
                    
                    <div className="aspect-[3/4] sm:aspect-[4/3] overflow-hidden">
                      <img 
                        src={image.src} 
                        alt={image.alt}
                        className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110 group-hover:rotate-1"
                        loading="lazy"
                      />
                    </div>
                    
                    {/* Gradient Overlay with sparkle effect */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                      <div className="absolute inset-0">
                        {/* Sparkle effect */}
                        {[...Array(3)].map((_, i) => (
                          <div
                            key={i}
                            className="absolute w-2 h-2 bg-white rounded-full animate-sparkle"
                            style={{
                              left: `${Math.random() * 80 + 10}%`,
                              top: `${Math.random() * 60 + 20}%`,
                              animationDelay: `${i * 0.3}s`
                            }}
                          />
                        ))}
                      </div>
                      
                      <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 right-4 sm:right-6 text-white transform translate-y-4 sm:translate-y-6 group-hover:translate-y-0 transition-transform duration-700">
                        <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-1 sm:mb-2 group-hover:scale-105 transition-transform duration-500">{image.caption}</h3>
                        <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm opacity-80">
                          <span className="flex items-center gap-1 backdrop-blur-sm bg-white/10 px-2 py-1 rounded-full">
                            <Camera className="w-3 h-3 sm:w-4 sm:h-4" />
                            {formatDate(image.date)}
                          </span>
                          <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-white/20 backdrop-blur-sm rounded-full group-hover:bg-white/30 transition-all duration-500">
                            {image.category}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Top Actions with glow effect */}
                    <div className="absolute top-3 sm:top-4 left-3 sm:left-4 right-3 sm:right-4 flex justify-between items-center transform -translate-y-3 sm:-translate-y-4 group-hover:translate-y-0 transition-transform duration-700">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleLike(image.id, e);
                        }}
                        className="p-2 sm:p-3 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-all duration-500 transform hover:scale-110 shadow-lg group/like relative overflow-hidden"
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-pink-400/0 to-rose-400/0 group-hover/like:from-pink-400/20 group-hover/like:to-rose-400/20 transition-all duration-500"></div>
                        <Heart 
                          className={`w-4 h-4 sm:w-5 sm:h-5 relative transition-all duration-500 ${
                            likedImages.has(image.id) 
                              ? 'fill-rose-500 text-rose-500 scale-110 animate-heart-pop' 
                              : 'text-gray-600'
                          }`}
                        />
                      </button>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          openLightbox(image);
                        }}
                        className="p-2 sm:p-3 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-all duration-500 transform hover:scale-110 shadow-lg group/expand relative overflow-hidden"
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-pink-400/0 to-rose-400/0 group-hover/expand:from-pink-400/20 group-hover/expand:to-rose-400/20 transition-all duration-500"></div>
                        <Maximize2 className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 relative" />
                      </button>
                    </div>
                    
                    {/* Like Indicator */}
                    <div className={`absolute top-3 sm:top-4 right-3 sm:right-4 px-2 py-1 sm:px-3 sm:py-1.5 rounded-full backdrop-blur-sm transition-all duration-500 transform ${
                      likedImages.has(image.id) 
                        ? 'bg-gradient-to-r from-rose-500/90 to-pink-500/90 text-white scale-100 shadow-lg' 
                        : 'bg-white/90 text-gray-600 scale-0'
                    }`}>
                      <span className="text-xs sm:text-sm font-medium flex items-center gap-1">
                        <Heart className="w-2 h-2 sm:w-3 sm:h-3 animate-pulse" />
                        <span className="hidden sm:inline">Loved</span>
                      </span>
                    </div>
                  </div>
                  
                  {/* Caption Card with animated border */}
                  <div className="p-3 sm:p-4 md:p-5 bg-gradient-to-br from-white via-pink-50/30 to-white relative overflow-hidden">
                    {/* Animated border */}
                    <div className="absolute inset-0 bg-gradient-to-r from-pink-400/0 via-rose-400/0 to-pink-400/0 group-hover:from-pink-400/10 group-hover:via-rose-400/10 group-hover:to-pink-400/10 transition-all duration-500"></div>
                    
                    <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-1 sm:mb-2 relative group-hover:scale-105 transition-transform duration-500">{image.caption}</h3>
                    <div className="flex justify-between items-center relative">
                      <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-gray-500">
                        <Camera className="w-3 h-3 sm:w-4 sm:h-4 animate-pulse-slow" />
                        <span>{formatDate(image.date)}</span>
                      </div>
                      <span className="px-2 py-0.5 sm:px-3 sm:py-1 bg-gradient-to-r from-pink-100 to-rose-100 text-pink-600 rounded-full text-xs font-medium transition-all duration-500 group-hover:scale-110 group-hover:shadow-md">
                        {image.category}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stats with floating effect */}
        {/* <div className="max-w-4xl mx-auto mt-12 sm:mt-16 animate-fade-in-stagger" style={{ animationDelay: '700ms' }}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 md:gap-6 px-4">
            {[
              { icon: Camera, value: images.length, label: 'Total Moments', color: 'from-pink-500 via-rose-500 to-pink-500' },
              { icon: Heart, value: likedImages.size, label: 'Loved Photos', color: 'from-rose-500 via-pink-500 to-rose-500' },
              { icon: Bookmark, value: '9/9', label: 'Collection', color: 'from-pink-400 via-rose-400 to-pink-400' },
              { icon: Sparkles, value: '∞', label: 'Memories', color: 'from-rose-400 via-pink-400 to-rose-400' },
            ].map((stat, index) => (
              <div 
                key={index}
                className="bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-pink-100 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 group relative overflow-hidden animate-stat-float"
                style={{ animationDelay: `${index * 100 + 800}ms` }}
              >
                
                <div className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-20 transition-opacity duration-500" 
                     style={{ background: `linear-gradient(to bottom right, ${stat.color.replace(/from-|via-|to-/g, '').split(' ')[0]}, ${stat.color.replace(/from-|via-|to-/g, '').split(' ')[2]})` }}></div>
                
                <div className={`w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center mb-2 sm:mb-4 group-hover:scale-110 transition-transform duration-500 relative overflow-hidden`}>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                  <stat.icon className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white relative" />
                </div>
                <div className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-1 group-hover:scale-105 transition-transform duration-500">{stat.value}</div>
                <div className="text-xs sm:text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div> */}

        {/* CTA with enhanced animation */}
        {/* <div className="text-center mt-12 sm:mt-16 animate-fade-in-stagger" style={{ animationDelay: '900ms' }}>
          <button className="group relative px-6 py-3 sm:px-8 sm:py-4 overflow-hidden rounded-xl sm:rounded-2xl font-bold hover:shadow-2xl transition-all duration-500 transform hover:scale-105 flex items-center gap-2 sm:gap-3 mx-auto text-sm sm:text-base animate-button-pulse">
            <div className="absolute inset-0 bg-gradient-to-r from-pink-500 via-rose-500 to-pink-500 animate-gradient-x"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-pink-600 via-rose-600 to-pink-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
            <Camera className="w-4 h-4 sm:w-5 sm:h-5 text-white relative" />
            <span className="text-white relative">Capture More Moments</span>
          </button>
          <p className="text-gray-500 text-xs sm:text-sm mt-4 sm:mt-6 animate-pulse-slow">
            Every moment is a story waiting to be told • Keep exploring
          </p>
        </div> */}

        {/* Scroll indicator for mobile with enhanced animation */}
        <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 z-20 md:hidden">
          <div className="flex flex-col items-center group cursor-pointer animate-bounce-slow">
            <div className="relative">
              <div className="absolute -inset-3 bg-gradient-to-b from-pink-400/20 to-transparent rounded-full blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <ArrowDown className="relative w-6 h-6 text-pink-500/70 group-hover:text-pink-600 group-hover:scale-125 transition-all duration-500" />
            </div>
            <div className="text-xs text-pink-600/70 mt-2 tracking-wider font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-500">
              Scroll for more
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Modal with enhanced animations */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 animate-modal-fade-in"
          onClick={closeLightbox}
        >
          {/* Animated backdrop */}
          <div className="absolute inset-0 bg-gradient-to-br from-black/95 via-black/90 to-black/95 backdrop-blur-xl transition-all duration-700"></div>
          
          {/* Floating particles in lightbox */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(10)].map((_, i) => (
              <div
                key={i}
                className="absolute rounded-full animate-lightbox-particle"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  width: `${Math.random() * 3 + 1}px`,
                  height: `${Math.random() * 3 + 1}px`,
                  background: `radial-gradient(circle, rgba(244, 114, 182, 0.6) 0%, rgba(244, 114, 182, 0) 70%)`,
                  animationDelay: `${i * 0.2}s`,
                  animationDuration: `${Math.random() * 10 + 5}s`,
                  filter: 'blur(0.5px)'
                }}
              />
            ))}
          </div>

          {/* Modal Content */}
          <div 
            className="relative z-10 max-w-6xl w-full max-h-[90vh] animate-modal-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button with glow */}
            <button 
              className="absolute -top-10 sm:-top-14 right-0 text-white hover:text-pink-300 transition-colors z-10 p-2 sm:p-3 bg-black/40 backdrop-blur-sm rounded-full hover:bg-black/60 hover:scale-110 transition-all duration-500 group"
              onClick={closeLightbox}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-pink-500/0 to-rose-500/0 rounded-full group-hover:from-pink-500/20 group-hover:to-rose-500/20 transition-all duration-500"></div>
              <X className="w-6 h-6 sm:w-8 sm:h-8 relative" />
            </button>
            
            {/* Navigation Buttons with enhanced effects */}
            <button 
              className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-2 sm:-translate-x-4 p-3 sm:p-4 text-white hover:text-pink-300 transition-colors z-10 bg-black/40 backdrop-blur-sm rounded-r-full hover:bg-black/60 hover:scale-110 transition-all duration-500 group"
              onClick={(e) => {
                e.stopPropagation();
                navigateImage('prev');
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-pink-500/0 to-rose-500/0 rounded-r-full group-hover:from-pink-500/20 group-hover:to-rose-500/20 transition-all duration-500"></div>
              <ChevronLeft className="w-6 h-6 sm:w-8 sm:h-8 relative" />
            </button>
            
            <button 
              className="absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-2 sm:translate-x-4 p-3 sm:p-4 text-white hover:text-pink-300 transition-colors z-10 bg-black/40 backdrop-blur-sm rounded-l-full hover:bg-black/60 hover:scale-110 transition-all duration-500 group"
              onClick={(e) => {
                e.stopPropagation();
                navigateImage('next');
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-l from-pink-500/0 to-rose-500/0 rounded-l-full group-hover:from-pink-500/20 group-hover:to-rose-500/20 transition-all duration-500"></div>
              <ChevronRight className="w-6 h-6 sm:w-8 sm:h-8 relative" />
            </button>
            
            {/* Image Container with enhanced border */}
            <div className="relative">
              <div className="absolute -inset-2 sm:-inset-4 bg-gradient-to-r from-pink-500 via-rose-500 to-pink-500 rounded-lg sm:rounded-xl blur-lg opacity-30 animate-gradient-x"></div>
              <img 
                src={selectedImage.src} 
                alt={selectedImage.alt}
                className="relative w-full max-h-[60vh] sm:max-h-[70vh] object-contain rounded-lg sm:rounded-xl shadow-2xl border-4 border-transparent bg-gradient-to-r from-pink-500 via-rose-500 to-pink-500 bg-clip-border"
              />
              
              {/* Image Info with gradient background */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/95 via-black/80 to-transparent p-4 sm:p-6 md:p-8 rounded-b-lg sm:rounded-b-xl">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-0">
                  <div>
                    <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-1 sm:mb-2 animate-fade-in">{selectedImage.caption}</h3>
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4 md:gap-6 text-white/80 text-sm sm:text-base">
                      <div className="flex items-center gap-1 sm:gap-2 backdrop-blur-sm bg-white/10 px-2 py-1 rounded-full">
                        <Camera className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span>{formatDate(selectedImage.date)}</span>
                      </div>
                      <span className="px-2 sm:px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full">
                        {selectedImage.category}
                      </span>
                      <span className="text-pink-200 animate-pulse">
                        Image {filteredImages.findIndex(img => img.id === selectedImage.id) + 1} of {filteredImages.length}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 sm:gap-3">
                    <button 
                      onClick={() => toggleLike(selectedImage.id)}
                      className="p-2 sm:p-3 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-all transform hover:scale-110 group relative overflow-hidden"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-pink-500/0 to-rose-500/0 group-hover:from-pink-500/20 group-hover:to-rose-500/20 transition-all duration-500"></div>
                      <Heart 
                        className={`w-5 h-5 sm:w-6 sm:h-6 relative ${likedImages.has(selectedImage.id) ? 'fill-rose-500 text-rose-500 scale-110 animate-heart-pop' : 'text-white'}`}
                      />
                    </button>
                    <button className="p-2 sm:p-3 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-all transform hover:scale-110 group relative overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-r from-pink-500/0 to-rose-500/0 group-hover:from-pink-500/20 group-hover:to-rose-500/20 transition-all duration-500"></div>
                      <Share2 className="w-5 h-5 sm:w-6 sm:h-6 text-white relative" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Thumbnail Strip with enhanced effects */}
            <div className="flex justify-center gap-2 sm:gap-3 mt-4 sm:mt-6 overflow-x-auto pb-2 animate-fade-in" style={{ animationDelay: '200ms' }}>
              {filteredImages.map((img) => (
                <button
                  key={img.id}
                  onClick={() => setSelectedImage(img)}
                  className={`flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-lg overflow-hidden transition-all duration-500 relative group ${
                    selectedImage.id === img.id 
                      ? 'ring-2 sm:ring-4 ring-pink-500 scale-110 shadow-lg' 
                      : 'opacity-70 hover:opacity-100 hover:scale-105'
                  }`}
                >
                  {/* Glow effect for active thumbnail */}
                  {selectedImage.id === img.id && (
                    <div className="absolute -inset-1 bg-gradient-to-r from-pink-500 to-rose-500 rounded-lg blur-sm opacity-50"></div>
                  )}
                  <img 
                    src={img.src} 
                    alt={img.alt}
                    className="w-full h-full object-cover relative"
                  />
                  {/* Hover effect */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </button>
              ))}
            </div>
          </div>
          
          {/* Instructions with fade effect */}
          <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 text-white/60 text-xs sm:text-sm text-center px-4 animate-fade-in" style={{ animationDelay: '400ms' }}>
            <div className="flex items-center gap-2 animate-pulse-slow">
              <span>Use arrows or tap thumbnails</span>
              <span className="text-pink-300">•</span>
              <span>Tap outside to close</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Enhanced CSS Animations with new effects
const styles = `
@keyframes bubble-float {
  0%, 100% {
    transform: translateY(0) translateX(0) scale(1) rotate(0deg);
    opacity: 0.3;
  }
  25% {
    transform: translateY(-40px) translateX(15px) scale(1.15) rotate(5deg);
    opacity: 0.7;
  }
  50% {
    transform: translateY(-70px) translateX(-10px) scale(1) rotate(0deg);
    opacity: 0.4;
  }
  75% {
    transform: translateY(-25px) translateX(20px) scale(1.1) rotate(-5deg);
    opacity: 0.6;
  }
}

@keyframes star-twinkle {
  0%, 100% {
    opacity: 0.3;
    transform: scale(1) rotate(0deg);
    filter: drop-shadow(0 0 2px rgba(255, 182, 193, 0.6));
  }
  25% {
    opacity: 0.9;
    transform: scale(1.3) rotate(90deg);
    filter: drop-shadow(0 0 5px rgba(255, 182, 193, 0.9));
  }
  50% {
    opacity: 0.4;
    transform: scale(0.95) rotate(180deg);
    filter: drop-shadow(0 0 3px rgba(255, 182, 193, 0.7));
  }
  75% {
    opacity: 0.8;
    transform: scale(1.2) rotate(270deg);
    filter: drop-shadow(0 0 4px rgba(255, 182, 193, 0.8));
  }
}

@keyframes sparkle-float {
  0%, 100% {
    transform: translateY(0) rotate(0deg) scale(1);
    opacity: 0.2;
  }
  50% {
    transform: translateY(-30px) rotate(180deg) scale(1.8);
    opacity: 0.9;
  }
}

@keyframes heart-float {
  0%, 100% {
    transform: translateY(0) scale(1) rotate(0deg);
    opacity: 0.2;
  }
  25% {
    transform: translateY(-50px) scale(1.2) rotate(45deg);
    opacity: 0.6;
  }
  50% {
    transform: translateY(-90px) scale(0.9) rotate(90deg);
    opacity: 0.3;
  }
  75% {
    transform: translateY(-35px) scale(1.1) rotate(135deg);
    opacity: 0.5;
  }
}

@keyframes diamond-float {
  0%, 100% {
    transform: translateY(0) rotate(45deg) scale(1);
    opacity: 0.3;
  }
  33% {
    transform: translateY(-60px) rotate(135deg) scale(1.3);
    opacity: 0.8;
  }
  66% {
    transform: translateY(-100px) rotate(225deg) scale(0.95);
    opacity: 0.4;
  }
}

@keyframes music-note {
  0%, 100% {
    transform: translateY(0) translateX(0) scale(1);
    opacity: 0.2;
  }
  25% {
    transform: translateY(-45px) translateX(20px) scale(1.2);
    opacity: 0.7;
  }
  50% {
    transform: translateY(-80px) translateX(-15px) scale(0.9);
    opacity: 0.3;
  }
  75% {
    transform: translateY(-30px) translateX(25px) scale(1.1);
    opacity: 0.6;
  }
}

@keyframes orb-float {
  0%, 100% {
    transform: translateY(0) rotate(0deg) scale(1);
  }
  50% {
    transform: translateY(-25px) rotate(-3deg) scale(1.05);
  }
}

@keyframes orb-float-slow {
  0%, 100% {
    transform: translateY(0) rotate(0deg) scale(1);
  }
  33% {
    transform: translateY(-15px) rotate(2deg) scale(1.03);
  }
  66% {
    transform: translateY(-10px) rotate(-1deg) scale(0.98);
  }
}

@keyframes wave {
  0%, 100% {
    transform: translateX(0) scaleY(1);
  }
  25% {
    transform: translateX(-25%) scaleY(1.15);
  }
  50% {
    transform: translateX(-50%) scaleY(0.9);
  }
  75% {
    transform: translateX(-75%) scaleY(1.1);
  }
}

@keyframes gradient-x {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

@keyframes gradient-flow {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

@keyframes slide-up-stagger {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fade-in-stagger {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes width-pulse {
  0%, 100% {
    width: 0;
    left: 50%;
  }
  50% {
    width: 100%;
    left: 0;
  }
}

@keyframes width-pulse-slow {
  0%, 100% {
    width: 0;
    left: 50%;
  }
  50% {
    width: 60%;
    left: 20%;
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
}

@keyframes pulse-slow {
  0%, 100% {
    opacity: 0.5;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes spin-slow {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes bounce-slow {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-15px);
  }
}

@keyframes button-float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
}

@keyframes card-float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes stat-float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
}

@keyframes heart-pop {
  0%, 100% {
    transform: scale(1);
  }
  25% {
    transform: scale(1.3);
  }
  50% {
    transform: scale(0.9);
  }
  75% {
    transform: scale(1.1);
  }
}

@keyframes button-pulse {
  0%, 100% {
    box-shadow: 0 10px 30px rgba(244, 114, 182, 0.3);
  }
  50% {
    box-shadow: 0 15px 40px rgba(244, 114, 182, 0.5);
  }
}

@keyframes sparkle {
  0%, 100% {
    opacity: 0;
    transform: scale(0);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

@keyframes modal-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes modal-slide-up {
  from {
    opacity: 0;
    transform: translateY(50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes lightbox-particle {
  0%, 100% {
    transform: translateY(0) translateX(0);
    opacity: 0.2;
  }
  50% {
    transform: translateY(-50px) translateX(20px);
    opacity: 0.8;
  }
}

/* Animation classes */
.animate-bubble-float {
  animation: bubble-float 25s ease-in-out infinite;
  will-change: transform, opacity;
}

.animate-star-twinkle {
  animation: star-twinkle 18s ease-in-out infinite;
  will-change: transform, opacity, filter;
}

.animate-sparkle-float {
  animation: sparkle-float 5s ease-in-out infinite;
  will-change: transform, opacity;
}

.animate-heart-float {
  animation: heart-float 20s ease-in-out infinite;
  will-change: transform, opacity;
}

.animate-diamond-float {
  animation: diamond-float 22s ease-in-out infinite;
  will-change: transform, opacity;
}

.animate-music-note {
  animation: music-note 15s ease-in-out infinite;
  will-change: transform, opacity;
}

.animate-orb-float {
  animation: orb-float 15s ease-in-out infinite;
  will-change: transform;
}

.animate-orb-float-slow {
  animation: orb-float-slow 20s ease-in-out infinite;
  will-change: transform;
}

.animate-wave {
  animation: wave 30s linear infinite;
  will-change: transform;
}

.animate-gradient-x {
  animation: gradient-x 3s ease infinite;
  background-size: 200% 200%;
  will-change: background-position;
}

.animate-gradient-flow {
  animation: gradient-flow 3s ease infinite;
  background-size: 200% auto;
  will-change: background-position;
}

.animate-slide-up-stagger {
  animation: slide-up-stagger 1s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.animate-fade-in-stagger {
  animation: fade-in-stagger 1s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.animate-width-pulse {
  animation: width-pulse 3s ease-in-out infinite;
  will-change: width, left;
}

.animate-width-pulse-slow {
  animation: width-pulse-slow 4s ease-in-out infinite;
  will-change: width, left;
}

.animate-pulse {
  animation: pulse 2s ease-in-out infinite;
  will-change: transform, opacity;
}

.animate-pulse-slow {
  animation: pulse-slow 3s ease-in-out infinite;
  will-change: transform, opacity;
}

.animate-spin {
  animation: spin 20s linear infinite;
  will-change: transform;
}

.animate-spin-slow {
  animation: spin-slow 30s linear infinite;
  will-change: transform;
}

.animate-bounce-slow {
  animation: bounce-slow 3s ease-in-out infinite;
  will-change: transform;
}

.animate-button-float {
  animation: button-float 6s ease-in-out infinite;
  will-change: transform;
}

.animate-card-float {
  animation: card-float 8s ease-in-out infinite;
  will-change: transform;
}

.animate-stat-float {
  animation: stat-float 7s ease-in-out infinite;
  will-change: transform;
}

.animate-heart-pop {
  animation: heart-pop 0.5s ease-in-out;
  will-change: transform;
}

.animate-button-pulse {
  animation: button-pulse 2s ease-in-out infinite;
  will-change: box-shadow;
}

.animate-sparkle {
  animation: sparkle 1.5s ease-in-out infinite;
  will-change: transform, opacity;
}

.animate-shimmer {
  animation: shimmer 2s infinite;
  will-change: transform;
}

.animate-modal-fade-in {
  animation: modal-fade-in 0.5s ease-out forwards;
}

.animate-modal-slide-up {
  animation: modal-slide-up 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.animate-lightbox-particle {
  animation: lightbox-particle 15s ease-in-out infinite;
  will-change: transform, opacity;
}

/* Performance optimizations */
* {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Reduced motion preference */
@media (prefers-reduced-motion: reduce) {
  .animate-bubble-float,
  .animate-star-twinkle,
  .animate-sparkle-float,
  .animate-heart-float,
  .animate-diamond-float,
  .animate-music-note,
  .animate-orb-float,
  .animate-orb-float-slow,
  .animate-wave,
  .animate-gradient-x,
  .animate-gradient-flow,
  .animate-slide-up-stagger,
  .animate-fade-in-stagger,
  .animate-width-pulse,
  .animate-width-pulse-slow,
  .animate-pulse,
  .animate-pulse-slow,
  .animate-spin,
  .animate-spin-slow,
  .animate-bounce-slow,
  .animate-button-float,
  .animate-card-float,
  .animate-stat-float,
  .animate-heart-pop,
  .animate-button-pulse,
  .animate-sparkle,
  .animate-shimmer,
  .animate-modal-fade-in,
  .animate-modal-slide-up,
  .animate-lightbox-particle {
    animation: none !important;
  }
}

/* Mobile optimizations */
@media (max-width: 768px) {
  .animate-bubble-float,
  .animate-star-twinkle,
  .animate-heart-float,
  .animate-diamond-float,
  .animate-music-note {
    animation-duration: 15s !important;
  }
  
  .animate-wave {
    animation: none;
  }
  
  .animate-button-float,
  .animate-card-float,
  .animate-stat-float {
    animation-duration: 4s !important;
  }
}

/* Performance optimizations for low-end devices */
@media (max-width: 640px) and (prefers-reduced-data: reduce) {
  .animate-bubble-float,
  .animate-star-twinkle,
  .animate-sparkle-float,
  .animate-heart-float,
  .animate-diamond-float,
  .animate-music-note,
  .animate-orb-float,
  .animate-orb-float-slow,
  .animate-wave {
    display: none !important;
  }
}

/* Smooth scrolling */
html {
  scroll-behavior: smooth;
}

/* Optimize for touch devices */
@media (hover: none) and (pointer: coarse) {
  .group:hover {
    transform: none !important;
  }
  
  .animate-bubble-float,
  .animate-star-twinkle,
  .animate-sparkle-float {
    animation-duration: 25s !important;
  }
}

/* Masonry grid responsiveness */
@media (max-width: 640px) {
  .columns-1 {
    column-count: 1;
  }
}

@media (min-width: 641px) and (max-width: 1024px) {
  .columns-2 {
    column-count: 2;
  }
}

@media (min-width: 1025px) {
  .columns-3 {
    column-count: 3;
  }
}
`;

// Add styles to document
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}

export default Moment;